import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridToolbar } from '@mui/x-data-grid';
import { Refresh, Chat, Close, Add, Assignment } from '@mui/icons-material';
import { fetchJson } from '../../../utils/fetchJson';
import { UserContext } from '../../../contexts/UserContext';

const PRIORITY_COLORS = { critical: 'error', high: 'warning', medium: 'info', low: 'default' };

export default function SupportTicketsPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('support-tickets');
  }, [user]);

  // Metrics
  const [metrics, setMetrics] = useState(null);

  // Filters
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Create ticket dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ subject: '', description: '', priority: 'medium', category: 'general', schoolId: '' });
  const [creating, setCreating] = useState(false);

  // System users for assignment
  const [systemUsers, setSystemUsers] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [status, setStatus] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [sending, setSending] = useState(false);

  const loadMetrics = useCallback(async () => {
    try {
      const data = await fetchJson('/api/super-admin/ticket-metrics');
      setMetrics(data);
    } catch (_) {}
  }, []);

  const loadSystemUsers = useCallback(async () => {
    try {
      const data = await fetchJson('/api/system-users');
      setSystemUsers(Array.isArray(data?.users) ? data.users : (Array.isArray(data) ? data : []));
    } catch (_) {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchJson('/api/support/tickets');
      setRows(Array.isArray(res?.tickets) ? res.tickets : []);
    } catch (e) {
      setError(e?.message || t('supportTickets.failedLoad', 'Failed to load tickets'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadMetrics(); loadSystemUsers(); }, [load, loadMetrics, loadSystemUsers]);

  const openTicket = useCallback(async (row) => {
    if (!row?.id) return;
    setError('');
    try {
      const res = await fetchJson(`/api/support/tickets/${row.id}`);
      setActiveTicket(res?.ticket || row);
      setMessages(Array.isArray(res?.messages) ? res.messages : []);
      setStatus((res?.ticket?.status || row?.status || '') || '');
      setMessageText('');
      setAttachments([]);
      setDialogOpen(true);
    } catch (e) {
      setError(e?.message || t('supportTickets.failedLoadTicket', 'Failed to load ticket'));
    }
  }, []);

  const closeTicket = () => {
    setDialogOpen(false);
    setActiveTicket(null);
    setMessages([]);
    setMessageText('');
    setAttachments([]);
    setStatus('');
  };

  const refreshTicket = useCallback(async () => {
    if (!activeTicket?.id) return;
    try {
      const res = await fetchJson(`/api/support/tickets/${activeTicket.id}`);
      setActiveTicket(res?.ticket || activeTicket);
      setMessages(Array.isArray(res?.messages) ? res.messages : []);
      setStatus((res?.ticket?.status || '') || status);
    } catch (_) {}
  }, [activeTicket, status]);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(files);
  };

  const sendMessage = async () => {
    if (!activeTicket?.id) return;
    if (!canManage) return;
    if (!messageText && (!attachments || attachments.length === 0)) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append('message', messageText);
      (attachments || []).forEach(f => form.append('attachments', f));
      await fetchJson(`/api/support/tickets/${activeTicket.id}/messages`, {
        method: 'POST',
        body: form
      });
      setMessageText('');
      setAttachments([]);
      await refreshTicket();
      await load();
    } catch (e) {
      setError(e?.message || t('supportTickets.failedSend', 'Failed to send message'));
    } finally {
      setSending(false);
    }
  };

  const saveStatus = async (nextStatus) => {
    if (!activeTicket?.id) return;
    if (!canManage) return;
    setSavingStatus(true);
    setError('');
    try {
      await fetchJson(`/api/support/tickets/${activeTicket.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus })
      });
      setStatus(nextStatus);
      await refreshTicket();
      await load();
    } catch (e) {
      setError(e?.message || t('supportTickets.failedUpdateStatus', 'Failed to update status'));
    } finally {
      setSavingStatus(false);
    }
  };

  const createTicket = async () => {
    if (!createForm.subject || !createForm.description) return;
    if (!canManage) return;
    setCreating(true);
    setError('');
    try {
      await fetchJson('/api/support/tickets/admin-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm)
      });
      setCreateOpen(false);
      setCreateForm({ subject: '', description: '', priority: 'medium', category: 'general', schoolId: '' });
      await load();
      await loadMetrics();
    } catch (e) {
      setError(e?.message || t('supportTickets.failedCreate', 'Failed to create ticket'));
    } finally {
      setCreating(false);
    }
  };

  const assignTicket = async (ticketId, assignedToId) => {
    setError('');
    if (!canManage) return;
    try {
      await fetchJson(`/api/support/tickets/${ticketId}/assign`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignedToId: assignedToId || null })
      });
      await refreshTicket();
      await load();
    } catch (e) {
      setError(e?.message || t('supportTickets.failedAssign', 'Failed to assign ticket'));
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      if (priorityFilter !== 'all' && r.priority !== priorityFilter) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      return true;
    });
  }, [rows, priorityFilter, statusFilter]);

  const columns = useMemo(() => {
    return [
      { field: 'ticketNumber', headerName: t('supportTickets.ticketNumber', 'Ticket #'), width: 140 },
      { field: 'subject', headerName: t('supportTickets.subject', 'Subject'), flex: 1, minWidth: 200 },
      {
        field: 'priority', headerName: t('supportTickets.priority', 'Priority'), width: 110,
        renderCell: (params) => (
          <Chip label={String(params.value || 'medium').toUpperCase()} size="small" color={PRIORITY_COLORS[params.value] || 'default'} />
        )
      },
      { field: 'category', headerName: t('supportTickets.category', 'Category'), width: 120 },
      {
        field: 'status', headerName: t('supportTickets.status', 'Status'), width: 120,
        renderCell: (params) => {
          const s = params.value || '';
          const color = s === 'open' ? 'error' : s === 'in_progress' ? 'warning' : s === 'resolved' ? 'success' : 'default';
          return <Chip label={s.replace('_', ' ').toUpperCase()} size="small" color={color} variant="outlined" />;
        }
      },
      {
        field: 'school', headerName: t('supportTickets.school', 'School'), width: 160,
        valueGetter: (params) => params?.row?.school?.name || params?.row?.schoolId || ''
      },
      {
        field: 'user', headerName: t('supportTickets.user', 'User'), width: 140,
        valueGetter: (params) => params?.row?.user?.name || params?.row?.userId || ''
      },
      {
        field: 'assignedTo', headerName: t('supportTickets.assignedTo', 'Assigned To'), width: 140,
        valueGetter: (params) => params?.row?.assignedTo?.name || (params?.row?.assignedToId ? `#${params.row.assignedToId}` : t('supportTickets.unassigned', 'Unassigned'))
      },
      {
        field: 'createdAt', headerName: t('supportTickets.created', 'Created'), width: 160,
        valueGetter: (params) => params?.row?.createdAt ? new Date(params.row.createdAt).toLocaleString() : ''
      },
      {
        field: 'actions', type: 'actions', width: 90,
        getActions: (params) => [
          <GridActionsCellItem key="open" icon={<Chat />} label={t('supportTickets.open', 'Open')} onClick={() => openTicket(params.row)} />
        ]
      }
    ];
  }, [openTicket]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>{t('supportTickets.title', 'Support Tickets')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<Add />} variant="contained" onClick={() => setCreateOpen(true)} disabled={!canManage}>{t('supportTickets.createTicket', 'Create Ticket')}</Button>
          <Button startIcon={<Refresh />} variant="outlined" onClick={() => { load(); loadMetrics(); }}>{t('supportTickets.refresh', 'Refresh')}</Button>
        </Stack>
      </Stack>

      {/* Metrics Cards */}
      {metrics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3} md={2}>
            <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h4" fontWeight={700} color="error.main">{metrics.open ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">{t('supportTickets.open', 'Open')}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">{metrics.in_progress ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">{t('supportTickets.inProgress', 'In Progress')}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h4" fontWeight={700} color="success.main">{metrics.resolved ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">{t('supportTickets.resolved', 'Resolved')}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h4" fontWeight={700}>{metrics.total ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">{t('supportTickets.total', 'Total')}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h4" fontWeight={700} color="info.main">{metrics.avgResolutionHours != null ? `${metrics.avgResolutionHours}h` : '—'}</Typography>
              <Typography variant="caption" color="text.secondary">{t('supportTickets.avgResolution', 'Avg Resolution')}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('supportTickets.priority', 'Priority')}</InputLabel>
          <Select value={priorityFilter} label={t('supportTickets.priority', 'Priority')} onChange={(e) => setPriorityFilter(e.target.value)}>
            <MenuItem value="all">{t('supportTickets.allPriorities', 'All Priorities')}</MenuItem>
            <MenuItem value="critical">{t('supportTickets.critical', 'Critical')}</MenuItem>
            <MenuItem value="high">{t('supportTickets.high', 'High')}</MenuItem>
            <MenuItem value="medium">{t('supportTickets.medium', 'Medium')}</MenuItem>
            <MenuItem value="low">{t('supportTickets.low', 'Low')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('supportTickets.status', 'Status')}</InputLabel>
          <Select value={statusFilter} label={t('supportTickets.status', 'Status')} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">{t('supportTickets.allStatuses', 'All Statuses')}</MenuItem>
            <MenuItem value="open">{t('supportTickets.open', 'Open')}</MenuItem>
            <MenuItem value="in_progress">{t('supportTickets.inProgress', 'In Progress')}</MenuItem>
            <MenuItem value="resolved">{t('supportTickets.resolved', 'Resolved')}</MenuItem>
            <MenuItem value="closed">{t('supportTickets.closed', 'Closed')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <div style={{ height: 560, width: '100%' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          components={{ Toolbar: GridToolbar }}
          disableSelectionOnClick
          onRowDoubleClick={(p) => openTicket(p.row)}
        />
      </div>

      <Dialog open={dialogOpen} onClose={closeTicket} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1}>
            <span>{activeTicket?.ticketNumber || t('supportTickets.ticket', 'Ticket')}</span>
            <Button startIcon={<Close />} onClick={closeTicket}>{t('supportTickets.close', 'Close')}</Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="h6">{activeTicket?.subject || ''}</Typography>
            <Typography variant="body2" color="text.secondary">
              {activeTicket?.school?.name ? `School: ${activeTicket.school.name}` : (activeTicket?.schoolId ? `School: ${activeTicket.schoolId}` : '')}
              {activeTicket?.user?.name ? ` • User: ${activeTicket.user.name}` : (activeTicket?.userId ? ` • User: ${activeTicket.userId}` : '')}
            </Typography>

            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="ticket-status">Status</InputLabel>
                <Select
                  labelId="ticket-status"
                  label="Status"
                  value={status || ''}
                  onChange={(e) => saveStatus(e.target.value)}
                  disabled={savingStatus || !canManage}
                >
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="ticket-assign">Assign To</InputLabel>
                <Select
                  labelId="ticket-assign"
                  label="Assign To"
                  value={activeTicket?.assignedToId || ''}
                  onChange={(e) => assignTicket(activeTicket?.id, e.target.value)}
                  disabled={!canManage}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {systemUsers.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.name || u.email} ({u.roleName || u.role})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, maxHeight: 320, overflowY: 'auto' }}>
              {messages.length === 0 ? (
                <Typography color="text.secondary">{t('supportTickets.noMessages', 'No messages')}</Typography>
              ) : (
                messages.map((m) => (
                  <Box key={m.id} sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {m.sender?.name || m.sender?.email || `User #${m.senderUserId || ''}`}
                      {m.createdAt ? ` • ${new Date(m.createdAt).toLocaleString()}` : ''}
                    </Typography>
                    <Typography variant="body1">{m.message}</Typography>
                    {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {m.attachments.map((a, idx) => (
                          <Chip
                            key={`${m.id}-att-${idx}`}
                            label={a.filename || a.originalName || a.url || 'attachment'}
                            component="a"
                            href={a.url || a.path}
                            target="_blank"
                            clickable
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                label={t('supportTickets.reply', 'Reply')}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                disabled={!canManage}
              />
              <Button variant="outlined" component="label" disabled={!canManage}>
                {t('supportTickets.attach', 'Attach')}
                <input hidden multiple type="file" onChange={onPickFiles} />
              </Button>
              <Button variant="contained" onClick={sendMessage} disabled={sending || !canManage}>
                {sending ? t('supportTickets.sending', 'Sending…') : t('supportTickets.send', 'Send')}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={refreshTicket}>{t('supportTickets.refresh', 'Refresh')}</Button>
        </DialogActions>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('supportTickets.createTicketTitle', 'Create Support Ticket')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('supportTickets.subject', 'Subject')}
              value={createForm.subject}
              onChange={(e) => setCreateForm(prev => ({ ...prev, subject: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label={t('supportTickets.description', 'Description')}
              value={createForm.description}
              onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              required
            />
            <Stack direction="row" spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('supportTickets.priority', 'Priority')}</InputLabel>
                <Select
                  label={t('supportTickets.priority', 'Priority')}
                  value={createForm.priority}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <MenuItem value="critical">{t('supportTickets.critical', 'Critical')}</MenuItem>
                  <MenuItem value="high">{t('supportTickets.high', 'High')}</MenuItem>
                  <MenuItem value="medium">{t('supportTickets.medium', 'Medium')}</MenuItem>
                  <MenuItem value="low">{t('supportTickets.low', 'Low')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('supportTickets.category', 'Category')}</InputLabel>
                <Select
                  label={t('supportTickets.category', 'Category')}
                  value={createForm.category}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, category: e.target.value }))}
                >
                  <MenuItem value="general">{t('supportTickets.general', 'General')}</MenuItem>
                  <MenuItem value="technical">{t('supportTickets.technical', 'Technical')}</MenuItem>
                  <MenuItem value="billing">{t('supportTickets.billing', 'Billing')}</MenuItem>
                  <MenuItem value="account">{t('supportTickets.account', 'Account')}</MenuItem>
                  <MenuItem value="feature_request">{t('supportTickets.featureRequest', 'Feature Request')}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label={t('supportTickets.schoolId', 'School ID (optional)')}
              value={createForm.schoolId}
              onChange={(e) => setCreateForm(prev => ({ ...prev, schoolId: e.target.value }))}
              fullWidth
              size="small"
              helperText={t('supportTickets.schoolIdHelper', 'Leave empty for platform-wide tickets')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('supportTickets.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={createTicket} disabled={creating || !createForm.subject || !createForm.description || !canManage}>
            {creating ? t('supportTickets.creating', 'Creating…') : t('supportTickets.create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
